import * as React from 'react';
import { Container } from '@autopylot-internal/dependency-injection';
import { DependencyContainerReactContext } from './DependencyContainerReactContext';

interface IProps {
    [key: string]: any; // allow consumers to add their own props when they sub-class
    container?: Container;
    children: React.ReactNode;
}

interface IState {
    doneInjecting: boolean;
}

class GlobalIOCHelper {
    constructor(private root:Container){
        (window as any).IOC = this;
    }
    get= (name: string) => {
        return this.root.getByStringName(name);
    };
    getAtElelement= (el: any, name: string) => {
        let container: Container | undefined =  el.container;
        while(!container){
            el = el.parentNode;
            if (!el){
                throw new Error('failed to find a container from the provided el and its parents');
            }
            container = el.container;
        }
        return container.getByStringName(name);
    };
}

export abstract class DependencyContainerContext extends React.Component<IProps, IState> {
    declare context: React.ContextType<typeof DependencyContainerReactContext>;

    private container: Container | undefined;

    private setContainerOnElement = (el: any) => {
        if (!el) {
            console.error('Unable to set container on undefined element');
            return;
        }
        el.container = this.container;
    };

    state = {
        doneInjecting: false,
    };

    async componentDidMount() {
        // If you didn't provide your own container and you didn't implement containerMounted
        // then you are just creating an empty child container, which is likely a mistake
        if (!this.props.container && !this.containerMounted) {
            throw new Error(((this as any).constructor as any).name + ' must implement containerMounted');
        }

        // Use the provided container
        if (this.props.container) {
            this.container = this.props.container;

            // Otherwise create a child container
        } else if (this.context.container) {
            this.container = this.context.container.createChild();

            // Otherwise create a new top-level container
        } else {
            this.container = new Container();
            new GlobalIOCHelper(this.container);
        }

        if (this.containerMounted) {
            await this.containerMounted(this.container!);
        }

        this.setState({ doneInjecting: true });
    }

    componentWillUnmount() {
        if (this.container) {
            this.container.dispose();
        }
    }

    abstract containerMounted(container: Container): void | Promise<void>;

    render() {
        const { doneInjecting } = (this as any).state;
        const { children } = this.props;

        if (!doneInjecting) {
            return (this as any).renderLoading();
        }

        return (
            <div data-container ref={this.setContainerOnElement} >
                <DependencyContainerReactContext.Provider value={{ container: this.container || null }}>
                    {children}
                </DependencyContainerReactContext.Provider>
            </div>
        );
    }

    renderLoading() {
        return null;
    }
}
